import React from "react"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import LayersOutlined from "@mui/icons-material/LayersOutlined"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExclamation } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { styles } from "./ConfirmationModal.styles"

const ConfirmationModalUI = ({
  open = false,
  onClose,
  icon = <LayersOutlined fontSize="medium" />,
  iconColor,
  iconBgColor,
  showIcon = true,
  showCloseButton,
  showDivider,
  title,
  description,
  children,
  primaryButton,
  secondaryButton,
  actions,
  maxWidth = 600,
  disableBackdropClick = true,
  variant = "centered", // alert, centered
}) => {
  const { t } = useTranslation()
  const isAlert = variant === "alert"
  const shouldShowCloseButton = showCloseButton ?? isAlert
  const shouldShowDivider = showDivider ?? isAlert
  const resolvedIconColor = iconColor || (isAlert ? "primary" : "blueBright")

  const handleClose = (event, reason) => {
    if (
      disableBackdropClick &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return
    }
    onClose?.(event, reason)
  }

  const defaultSecondary = {
    label: t("cancel"),
    onClick: onClose,
    variant: "outlined",
    color: isAlert ? "inherit" : "blueBright",
  }

  const defaultPrimary = {
    label: t("accept"),
    onClick: onClose,
    variant: "contained",
    color: isAlert ? "primary" : "blueBright",
  }

  const buttonsToRender =
    actions ||
    [
      secondaryButton !== false && secondaryButton !== null
        ? {
            ...defaultSecondary,
            ...(typeof secondaryButton === "object" ? secondaryButton : {}),
            ...(typeof secondaryButton === "string"
              ? { label: secondaryButton }
              : {}),
          }
        : null,
      primaryButton !== false && primaryButton !== null
        ? {
            ...defaultPrimary,
            ...(typeof primaryButton === "object" ? primaryButton : {}),
            ...(typeof primaryButton === "string"
              ? { label: primaryButton }
              : {}),
          }
        : null,
    ].filter(Boolean)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={disableBackdropClick}
      PaperProps={{ sx: styles.paper(isAlert ? 800 : maxWidth) }}
    >
      {shouldShowCloseButton && (
        <IconButton
          sx={styles.closeButton}
          aria-label={t("close", "Close")}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogContent sx={styles.content(isAlert)}>
        <Box sx={styles.header(isAlert)}>
          {showIcon && (isAlert || icon) && (
            <Box
              sx={styles.iconWrapper(resolvedIconColor, iconBgColor, isAlert)}
            >
              {isAlert ? <FontAwesomeIcon icon={faExclamation} /> : icon}
            </Box>
          )}
          {title && (
            <Typography
              variant={isAlert ? "h6" : "title"}
              sx={styles.title(isAlert)}
            >
              {title}
            </Typography>
          )}
        </Box>
        {description && (
          <Typography variant="body2" sx={styles.description(isAlert)}>
            {description}
          </Typography>
        )}
        {children}
        {shouldShowDivider && <Box sx={styles.divider} />}
        {buttonsToRender.length > 0 && (
          <Box sx={styles.actions(isAlert)}>
            {buttonsToRender.map((btn, index) => {
              const {
                label,
                onClick,
                variant = "contained",
                color = isAlert
                  ? variant === "outlined"
                    ? "inherit"
                    : "primary"
                  : "blueBright",
                disabled = false,
                disableElevation = true,
                sx,
                ...rest
              } = btn

              return (
                <Button
                  key={index}
                  variant={variant}
                  color={color}
                  onClick={onClick}
                  disabled={disabled}
                  disableElevation={disableElevation}
                  sx={[
                    styles.actionBtn(isAlert, variant),
                    ...(Array.isArray(sx) ? sx : [sx]),
                  ]}
                  {...rest}
                >
                  {label}
                </Button>
              )
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export { ConfirmationModalUI }
export default ConfirmationModalUI
